import React, { useEffect, useRef, useState } from "react";

import { message } from "antd";

import Swal from "sweetalert2";

import LdasApi from "../../../apis/Ldas.api";

import { LDAS_DELETE_SWAL_CONFIG } from "./LdasUtils";

const editIconSrc = `/assets/images/pencil-icon.png`;
const deleteIconSrc = `/assets/images/trash-icon.png`;

const LdasProductCategoryPanel = ({
  categories = [],
  onCategoriesInvalidated,
}) => {
  const [items, setItems] = useState(categories || []);
  const [editingVisualId, setEditingVisualId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [isSavingNew, setIsSavingNew] = useState(false);

  const newInputRef = useRef(null);
  const isSavingNewRef = useRef(false);
  const rowInputRefs = useRef({});
  const nameAtFocusRef = useRef({});
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    setItems(categories || []);
  }, [categories]);

  useEffect(() => {
    if (editingVisualId === null) return;
    const el = rowInputRefs.current[Number(editingVisualId)];
    el?.focus();
    el?.select();
  }, [editingVisualId]);

  useEffect(() => {
    if (isAdding) {
      newInputRef.current?.focus();
    }
  }, [isAdding]);

  const handleRowNameChange = (id, value) => {
    const n = Number(id);
    setItems((prev) =>
      prev.map((c) => (Number(c.id) === n ? { ...c, name: value } : c)),
    );
  };

  const handleRowNameFocus = (cat) => {
    nameAtFocusRef.current[Number(cat.id)] = cat?.name ?? "";
  };

  // 카테고리 이름 수정 (포커스 벗어날 때)
  const handleRowNameBlur = async (id) => {
    const n = Number(id);
    if (Number.isNaN(n)) return;

    const row = itemsRef.current.find((c) => Number(c.id) === n);
    if (!row) return;

    const originalName = nameAtFocusRef.current[n] ?? row.name ?? "";
    const beforeTrim = String(originalName).trim();
    const inputEl = rowInputRefs.current[n];
    const raw = inputEl?.value ?? row.name;
    const name =
      String(raw).trim() || beforeTrim || String(row.name ?? "").trim();

    setItems((prev) =>
      prev.map((c) => (Number(c.id) === n ? { ...c, name } : c)),
    );
    setEditingVisualId((cur) =>
      cur !== undefined && cur !== null && Number(cur) === n ? null : cur,
    );

    // 기존 이름과 수정된 이름 같으면 update 요청 안함
    if (name === beforeTrim) return;

    try {
      const res = await LdasApi.updateLdasCategory(row.id, { name });
      // console.log("updateLdasCategory res", res);
      if (res.code === 200) {
        if (onCategoriesInvalidated) {
          await onCategoriesInvalidated();
        }
        return;
      } else {
        message.error(res?.message ?? "카테고리 이름 수정에 실패했습니다");
        setItems((prev) =>
          prev.map((c) =>
            Number(c.id) === n ? { ...c, name: originalName } : c,
          ),
        );
      }
    } catch (error) {
      console.error("updateLdasCategory", error);
      message.error("카테고리 이름 수정에 실패했습니다");
      setItems((prev) =>
        prev.map((c) =>
          Number(c.id) === n ? { ...c, name: originalName } : c,
        ),
      );
    }
  };

  const handlePencilClick = (id) => {
    setEditingVisualId(id);
  };

  const handleDeleteClick = async (cat) => {
    const result = await Swal.fire({
      ...LDAS_DELETE_SWAL_CONFIG,
      title: `"${cat.name}" 카테고리를 삭제할까요?`,
      text: "정말로 이 카테고리를 삭제하시겠습니까? 사용 중인 카테고리는 삭제할 수 없습니다.",
    });
    if (!result.isConfirmed) return;

    const nid = Number(cat.id);

    try {
      const res = await LdasApi.deleteLdasCategory(nid);
      // console.log("deleteLdasCategory res", res);
      if (res.code === 200) {
        setItems((prev) => prev.filter((c) => Number(c.id) !== nid));
        setEditingVisualId((cur) =>
          cur !== undefined && cur !== null && Number(cur) === nid ? null : cur,
        );
        delete rowInputRefs.current[nid];
        delete nameAtFocusRef.current[nid];
        if (onCategoriesInvalidated) {
          await onCategoriesInvalidated();
        }
        return;
      } else {
        message.error(res?.message ?? "카테고리 삭제에 실패했습니다");
      }
    } catch (error) {
      console.error("deleteLdasCategory", error);
      message.error("카테고리 삭제에 실패했습니다");
    }
  };

  const handleAddBtnClick = () => {
    if (isAdding) {
      newInputRef.current?.focus();
      return;
    }
    setIsAdding(true);
    setNewTagName("");
  };

  // 새 카테고리 저장
  const handleSaveNew = async () => {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    if (isSavingNewRef.current) return;

    isSavingNewRef.current = true;
    setIsSavingNew(true);

    const sortOrder = items.length + 1;

    try {
      const res = await LdasApi.createLdasCategory({
        name: trimmed,
        sortOrder,
      });
      if (res.code === 200) {
        setItems((prev) => {
          const id = res.result;
          if (
            id !== undefined &&
            id !== null &&
            id !== "" &&
            prev.some((c) => Number(c.id) === Number(id))
          ) {
            return prev;
          }
          return [...prev, res.result];
        });
        if (onCategoriesInvalidated) {
          await onCategoriesInvalidated();
        }
        setIsAdding(false);
        setNewTagName("");
        return;
      }
      message.error(res?.message ?? "카테고리 저장에 실패했습니다");
    } catch (error) {
      console.error("createLdasCategory", error);
      message.error("카테고리 저장에 실패했습니다");
    } finally {
      isSavingNewRef.current = false;
      setIsSavingNew(false);
    }
  };

  // 새 카테고리 저장 키 입력
  const handleNewInputKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (isSavingNewRef.current) return;
    e.preventDefault();
    handleSaveNew();
  };

  return (
    <div className="ldas-cat-panel">
      <div className="ldas-cat-panel__header">
        <span className="ldas-cat-panel__title">카테고리 관리</span>
        <button
          type="button"
          className="ldas-cat-panel__add-btn"
          onClick={handleAddBtnClick}
          disabled={isSavingNew}
          aria-label="카테고리 추가"
        >
          +
        </button>
      </div>

      <ul className="ldas-cat-panel__list">
        {items.map((cat, idx) => (
          <li
            key={
              cat !== undefined &&
              cat !== null &&
              cat.id !== undefined &&
              cat.id !== null &&
              cat.id !== ""
                ? `cat-${String(cat.id)}`
                : `cat-row-${idx}`
            }
            className="ldas-cat-panel__item"
          >
            <span className="ldas-cat-panel__num">{idx + 1}</span>
            <input
              ref={(el) => {
                rowInputRefs.current[Number(cat.id)] = el;
              }}
              type="text"
              readOnly={
                editingVisualId === null ||
                Number(editingVisualId) !== Number(cat.id)
              }
              className={[
                "ldas-cat-panel__name-input",
                editingVisualId !== null &&
                Number(editingVisualId) === Number(cat.id)
                  ? "ldas-cat-panel__name-input--editing"
                  : "ldas-cat-panel__name-input--plain",
              ].join(" ")}
              value={cat.name}
              onChange={(e) => handleRowNameChange(cat.id, e.target.value)}
              onFocus={() => handleRowNameFocus(cat)}
              onBlur={() => handleRowNameBlur(cat.id)}
              aria-label={`${cat.name} 카테고리 이름`}
            />
            {/* <span
              className={
                cat.productCount > 0
                  ? "ldas-cat-panel-badge ldas-cat-panel-badge--active"
                  : "ldas-cat-panel-badge"
              }
            >
              {cat.productCount > 0 ? `${cat.productCount}개 제품` : "미사용"}
            </span> */}
            <div className="ldas-cat-panel__actions">
              <button
                type="button"
                className="ldas-cat-panel__action-btn"
                title="이름 수정"
                aria-label={`${cat.name} 이름 수정`}
                onClick={() => handlePencilClick(cat.id)}
              >
                <img
                  src={editIconSrc}
                  alt=""
                  style={{ width: 16, height: 16 }}
                />
              </button>
              <button
                type="button"
                className="ldas-cat-panel__action-btn"
                title="삭제"
                aria-label={`${cat.name} 삭제`}
                onClick={() => handleDeleteClick(cat)}
              >
                <img
                  src={deleteIconSrc}
                  alt=""
                  style={{ width: 16, height: 16 }}
                />
              </button>
            </div>
          </li>
        ))}

        {isAdding && (
          <li
            key="ldas-cat-panel-new"
            className="ldas-cat-panel__item ldas-cat-panel__item--new"
          >
            <span className="ldas-cat-panel__num">{items.length + 1}</span>
            <input
              ref={newInputRef}
              type="text"
              className="ldas-cat-panel__name-input ldas-cat-panel__name-input--new"
              placeholder="태그 이름"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={handleNewInputKeyDown}
              disabled={isSavingNew}
              aria-busy={isSavingNew}
              aria-label="새 카테고리 이름"
            />
            <button
              type="button"
              className="ldas-cat-panel__save-btn"
              onClick={handleSaveNew}
              disabled={isSavingNew}
              aria-busy={isSavingNew}
            >
              {isSavingNew ? "저장 중…" : "저장"}
            </button>
          </li>
        )}
      </ul>
    </div>
  );
};

export default LdasProductCategoryPanel;
